<template>
  <div class="relative flex justify-between items-center px-3 py-3 border-t">
    <div class="my-auto text-sm text-gray-brand-600 rounded-full tabular-nums">
      <transition name="strike" :duration="{ enter: 850, leave: 600 }">
        <span v-if="showStrikethroughPrice && showPrice" class="strike-wrap inline-block">
          <s>{{ defaultPrice?.format }}</s>
          &nbsp;
        </span>
      </transition>
      <span v-if="showFreePrice" class="font-bold inline-block">Free</span>
      <span v-else-if="showPrice" class="font-bold inline-block" :class="activePriceFadeClass">
        {{ price?.format }}
      </span>
    </div>
    <ProductQuantities
      v-if="openForOrders"
      :product="product"
      :submittable-cart-item="submittableCartItem"
      :category-product-store-pair="categoryProductStorePair"
      :is-upsell="isUpsell"
    />
  </div>
</template>

<script>
import { ProductQuantities } from '@/components'
import mapRelationship from '../../utils/mapRelationship'
import mapRelationships from '../../utils/mapRelationships'

export default {
  components: { ProductQuantities },

  props: {
    product: {
      required: true
    },
    submittableCartItem: {
      required: false
    },
    categoryProductStorePair: {
      required: false
    }
  },

  data() {
    return { strikeAnimDirection: null }
  },

  watch: {
    showStrikethroughPrice(newVal, oldVal) {
      if (newVal && !oldVal) this.strikeAnimDirection = 'enter'
      else if (!newVal && oldVal) this.strikeAnimDirection = 'leave'
      else return
      if (this._strikeAnimTimer) clearTimeout(this._strikeAnimTimer)
      this._strikeAnimTimer = setTimeout(() => {
        this.strikeAnimDirection = null
      }, 1500)
    }
  },

  beforeUnmount() {
    if (this._strikeAnimTimer) clearTimeout(this._strikeAnimTimer)
  },

  computed: {
    activePriceFadeClass() {
      if (this.strikeAnimDirection === 'enter') return 'fade-after-strike-draw'
      return ''
    },
    currentCart() {
      return this.$store.getters.currentCart
    },
    currentBottle() {
      return this.$store.getters.currentBottle
    },
    statusForPlacingOrders() {
      return this.currentBottle?.attributes?.status_for_placing_orders?.status
    },
    openForOrders() {
      return this.statusForPlacingOrders === 'open'
    },
    membershipTierID() {
      return this.$store.getters.currentCart?.relationships.membership_tier?.data?.id
    },
    pricingTierID() {
      return this.$store.getters.currentCart?.relationships.pricing_tier?.data?.id
    },
    isUpsell() {
      return (
        mapRelationship(this.$store.state, this.categoryProductStorePair?.relationships.category.data)?.attributes
          ?.type === 'UpsellCategory'
      )
    },
    price() {
      if (this.isUpsell) {
        if (this.submittableCartItem?.net_price_plus_net_variants_price?.cents > 0) {
          return this.submittableCartItem.net_price_plus_net_variants_price
        }
        const upsellCents = this.categoryProductStorePair?.attributes?.default_upsell_price
        if (upsellCents) return upsellCents
      }
      if (this.productPriceListPair) {
        return this.productPriceListPair.attributes.price
      } else if (this.submittableCartItem?.net_price_plus_net_variants_price?.cents > 0) {
        return this.submittableCartItem.net_price_plus_net_variants_price
      } else {
        return this.categoryProductStorePair?.attributes?.product_price_tiers?.find(
          (item) => item.membership_tier_id == this.membershipTierID && item.pricing_tier_id == this.pricingTierID
        )
      }
    },
    productPriceListPair() {
      return mapRelationships(
        this.$store.state,
        this.currentBottle?.relationships?.product_price_list_pairs?.data
      )?.find((item) => item.relationships.product?.data?.id?.toString() == this.product?.id?.toString())
    },
    defaultMembershipTierID() {
      return this.$store.getters.membershipTiers?.find((item) => item.attributes.is_default)?.attributes.id
    },
    defaultPricingTierID() {
      return this.$store.getters.pricingTiers?.find((item) => item.attributes.is_default)?.attributes.id
    },
    defaultPrice() {
      if (
        this.submittableCartItem?.default_price_plus_variants_price?.cents &&
        this.submittableCartItem?.membership_price_plus_variants_price?.cents &&
        this.submittableCartItem?.default_price_plus_variants_price?.cents >
          this.submittableCartItem?.membership_price_plus_variants_price?.cents
      )
        return this.submittableCartItem.default_price_plus_variants_price
      if (
        this.submittableCartItem?.membership_price_plus_variants_price?.cents &&
        this.submittableCartItem?.membership_price_plus_variants_price?.cents > 0
      )
        return this.submittableCartItem.membership_price_plus_variants_price
      let defaultPriceObject = this.categoryProductStorePair?.attributes?.product_price_tiers?.find(
        (item) =>
          item.membership_tier_id == this.defaultMembershipTierID && item.pricing_tier_id == this.defaultPricingTierID
      )
      if (
        this.product.attributes?.default_price?.cents &&
        this.product.attributes?.default_price?.cents > defaultPriceObject?.cents
      )
        return this.product.attributes.default_price
      return defaultPriceObject
    },
    showStrikethroughPrice() {
      return this.defaultPrice && this.price && this.defaultPrice.cents > this.price.cents
    },
    showFreePrice() {
      if (!this.categoryProductStorePair.attributes.show_prices) return false
      return this.price?.cents == 0 && this.product?.relationships?.variants?.data?.length == 0
    },
    showPrice() {
      if (!this.categoryProductStorePair.attributes.show_prices) return false
      return this.product?.relationships?.variants?.data?.length == 0 || this.price?.cents > 0
    }
  }
}
</script>

<style>
.strike-wrap s {
  text-decoration: none;
  position: relative;
  display: inline-block;
}
.strike-wrap s::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 1px;
  background: currentColor;
  transform: scaleX(0);
  transform-origin: left center;
  animation: strike-draw 0.6s 0.25s ease-out forwards;
}
.strike-leave-active s::after {
  animation: strike-undraw 0.6s ease-in both;
}
@keyframes strike-draw {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}
@keyframes strike-undraw {
  from {
    transform: scaleX(1);
  }
  to {
    transform: scaleX(0);
  }
}
.fade-after-strike-draw {
  animation: fade-after-strike-draw 1.35s ease-out forwards;
}
@keyframes fade-after-strike-draw {
  0%,
  63% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
